import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, List, Typography, IconButton, InputBase, Container, Button, CircularProgress } from '@mui/material';
import { useDeepCompareMemo, useDeepCompareEffect } from 'use-deep-compare';
import { useNavigate } from 'react-router-dom';
import { Search, Add, Close } from '@mui/icons-material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { MessageDialogue } from '../../../components/MessageDialogue/MessageDialogue';
import { ROUTES } from '../../../types/global/routes.types';
import { useMessageContext } from '../../../hook/useMessageContext';
import { IRoom, IPagination } from '../../../redux/messages/types';
import RoomItem from '../../../components/RoomItem/RoomItem';
import RoomItemSkeleton from '../../../components/RoomItem/RoomItemSkeleton';
import { useDispatch, useSelector } from 'react-redux';
import { userHasReadMessage, searchRooms, getMyRooms } from '../../../redux/messages/actions';
import { motion, AnimatePresence } from 'framer-motion';
import { getJWtDetails } from '../../../utils/AppUtils';
import { debounce } from 'lodash';
import { fetchingRoomsSelector, searchingSelector } from '../../../selectors/selectors';

interface IConversationsListProps {
    rooms: IRoom[];
    activeRoomId: string | null;
    onRoomClicked?: (roomId: string) => void;
    pagination: IPagination;
}

const ConversationsList: React.FC<IConversationsListProps> = ({ rooms, activeRoomId, onRoomClicked, pagination }) => {
    const { themeStyle } = useThemeContext();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [newMessageOpen, setNewMessageOpen] = useState(false);
    const { setMessage } = useMessageContext();
    const [loadingMore, setLoadingMore] = useState(false);
    const { user_id } = getJWtDetails();

    // Add search state
    const [searchTerm, setSearchTerm] = useState('');
    const isSearching = useSelector(searchingSelector);
    const isLoading = useSelector(fetchingRoomsSelector);

    const previousTimestampsRef = useRef<Record<string, string>>({});
    const updatedRoomIds = useRef<Set<string>>(new Set());

    const sortedRooms = useDeepCompareMemo(() => {
        return [...rooms].sort((a, b) => {
            const aIsThspian = a?.sender?.userName?.toLowerCase() === 'thspian';
            const bIsThspian = b?.sender?.userName?.toLowerCase() === 'thspian';

            // Pin thspian sender to the very top
            if (aIsThspian && !bIsThspian) return -1;
            if (!aIsThspian && bIsThspian) return 1;

            // Fallback: sort by last_message_at (newer first)
            const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
            const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;

            return bTime - aTime;
        });
    }, [rooms]);

    useDeepCompareEffect(() => {
        const newTimestamps: Record<string, string> = {};
        const updatedIds = new Set<string>();

        for (const room of rooms) {
            const prevTimestamp = previousTimestampsRef.current[room._id];
            const newTimestamp = room.last_message_at;

            if (prevTimestamp && prevTimestamp !== newTimestamp) {
                updatedIds.add(room._id);
            }
            newTimestamps[room._id] = newTimestamp;
        }

        previousTimestampsRef.current = newTimestamps;
        updatedRoomIds.current = updatedIds;
    }, [sortedRooms, rooms]);

    const handleRoomClick = (roomId: string) => {
        setMessage('');
        onRoomClicked?.(roomId);
        handleUserRead(roomId);
        navigate(`${ROUTES.MESSAGES}/${roomId}`);
    };

    const handleNewMessage = () => {
        setNewMessageOpen(true);
    };

    const handleLoadRooms = useCallback(() => {
        dispatch(getMyRooms({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        handleLoadRooms();
    }, [handleLoadRooms]);

    const handleLoadMore = useCallback(() => {
        if (!pagination.hasNextPage || loadingMore) return;

        setLoadingMore(true);

        // Use search or regular load based on whether we're in search mode
        if (searchTerm) {
            dispatch(
                searchRooms({
                    searchTerm,
                    page: pagination.currentPage + 1,
                    limit: pagination.limit,
                }),
            );
        } else {
            dispatch(
                getMyRooms({
                    limit: 10,
                    page: pagination.currentPage + 1,
                }),
            );
        }

        setLoadingMore(false);
    }, [pagination, dispatch, loadingMore, searchTerm]);

    const handleUserRead = (room: string) => {
        dispatch(
            userHasReadMessage({
                readBy: user_id,
                roomID: room,
            }),
        );
    };

    // Debounced search handler
    const debouncedSearch = useRef(
        debounce((term: string) => {
            if (term.trim() === '') {
                handleLoadRooms(); // Reset to all rooms when search is cleared
            } else {
                // Only dispatch search if we're not already searching
                if (!isSearching) {
                    dispatch(
                        searchRooms({
                            searchTerm: term,
                            limit: pagination.limit,
                        }),
                    );
                }
            }
        }, 500),
    ).current;

    // Search input handler
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    // Clear search
    const handleClearSearch = () => {
        setSearchTerm('');
        handleLoadRooms();
    };

    return (
        <Container
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
            {/* Header */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '60px',
                    p: 2,
                    pt: 4,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme[themeStyle].grey}`,
                    zIndex: 10,
                    backgroundColor: theme[themeStyle].secondaryBg,
                }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: theme[themeStyle].tag_white,
                    }}>
                    Messages
                </Typography>
                <IconButton onClick={handleNewMessage} sx={{ color: theme[themeStyle].highlight }}>
                    <Add />
                </IconButton>
            </Box>

            {/* Search input */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'absolute',
                    top: '60px',
                    left: 0,
                    right: 0,
                    height: '60px',
                    p: 2,
                    backgroundColor: theme[themeStyle].secondaryBg,
                    borderBottom: `1px solid ${theme[themeStyle].grey}`,
                    zIndex: 9,
                    width: '100%',
                }}>
                <Box
                    sx={{
                        p: '2px 4px',
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: theme[themeStyle].deep_grey,
                        borderRadius: 1,
                        width: '100%',
                    }}>
                    <Search sx={{ m: 1, color: theme[themeStyle].grey }} />
                    <InputBase
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search conversations..."
                        sx={{
                            ml: 1,
                            flex: 1,
                            color: theme[themeStyle].tag_white,
                            width: '100%',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 12,
                        }}
                    />
                    {searchTerm && (
                        <IconButton size="small" onClick={handleClearSearch} sx={{ color: theme[themeStyle].grey }}>
                            <Close fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Conversation List */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '116px',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflowY: 'auto',
                    bgcolor: theme[themeStyle].secondaryBg,
                }}>
                {isLoading || isSearching ? (
                    <List sx={{ p: 0 }}>
                        {Array(6)
                            .fill(0)
                            .map((_, index) => (
                                <RoomItemSkeleton key={`skeleton-${index}`} showDivider />
                            ))}
                    </List>
                ) : sortedRooms.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].grey,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}>
                            {searchTerm ? 'No conversations match your search' : 'No conversations yet'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <List sx={{ p: 0 }}>
                            <AnimatePresence initial={false}>
                                {sortedRooms.map(room => (
                                    <motion.div
                                        key={room._id}
                                        layout
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.25 }}
                                        style={{
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            backgroundColor: updatedRoomIds.current.has(room._id)
                                                ? theme[themeStyle].highlight + '22'
                                                : 'transparent',
                                        }}>
                                        <RoomItem
                                            onClick={() => handleRoomClick(room._id)}
                                            room={room}
                                            isActive={activeRoomId === room._id}
                                            showDivider
                                        />
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </List>

                        {/* Load More Button */}
                        {pagination.hasNextPage && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Button
                                    onClick={handleLoadMore}
                                    disabled={loadingMore}
                                    sx={{
                                        color: theme[themeStyle].highlight,
                                        '&:hover': {
                                            bgcolor: theme[themeStyle].deep_grey,
                                        },
                                    }}>
                                    {loadingMore ? (
                                        <CircularProgress size={24} sx={{ color: theme[themeStyle].true_white }} />
                                    ) : (
                                        'Load More'
                                    )}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* New Message Dialog */}
            {newMessageOpen && (
                <MessageDialogue open={newMessageOpen} fromModal onClose={() => setNewMessageOpen(false)} />
            )}
        </Container>
    );
};

export default ConversationsList;
